/**
 * HTML Sanitizer for Template Builder
 *
 * Removes dangerous HTML tags and attributes to prevent XSS attacks
 * Uses DOMPurify for maximum security
 */

import DOMPurify from "dompurify";

/**
 * Whitelist of allowed HTML tags
 * Only basic formatting tags that are safe and supported by React-PDF
 */
const ALLOWED_TAGS = [
  // Text formatting
  "p",
  "br",
  "span",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  // Lists
  "ul",
  "ol",
  "li",
  // Text styling
  "strong",
  "b",
  "em",
  "i",
  "u",
  // Tables
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  // Media
  "img",
  // Other
  "blockquote",
  "pre",
  "code",
  "hr"
];

/**
 * Whitelist of allowed HTML attributes
 * Only safe attributes without event handlers
 */
const ALLOWED_ATTRIBUTES = [
  "id",
  "class",
  "style", // Inline styles (will be validated separately)
  "align",
  "colspan",
  "rowspan",
  "data-fullpage", // For fullpage background images in PDF
  // Image attributes
  "src",
  "alt",
  "width",
  "height"
];

/**
 * Maximum allowed HTML size (50KB)
 */
const MAX_HTML_SIZE = 51_200; // 50 * 1024

/**
 * Validation result
 */
export interface SanitizationResult {
  /** Sanitized HTML (safe to use) */
  html: string;
  /** Whether any dangerous content was removed */
  hadDangerousContent: boolean;
  /** List of issues found */
  issues: string[];
  /** Whether the HTML is valid */
  isValid: boolean;
}

/**
 * Sanitize HTML by removing dangerous tags and attributes
 * @param html - Raw HTML string
 * @returns Sanitization result
 */
export function sanitizeHTML(html: string): SanitizationResult {
  // Check size limit
  if (html.length > MAX_HTML_SIZE) {
    return {
      html: "",
      hadDangerousContent: true,
      issues: [`HTML exceeds maximum size of ${MAX_HTML_SIZE} bytes (${html.length} bytes)`],
      isValid: false
    };
  }

  const issues: string[] = [];

  // Check for specific dangerous content before sanitization
  if (/<script\b/i.test(html)) {
    issues.push("Removed <script> tags");
  }

  if (/<iframe\b/i.test(html)) {
    issues.push("Removed <iframe> tags");
  }

  if (/<(object|embed)\b/i.test(html)) {
    issues.push("Removed <object> or <embed> tags");
  }

  if (/\s+on\w+\s*=/i.test(html)) {
    issues.push("Removed event handler attributes");
  }

  // Check for disallowed tags
  const allTags = html.match(/<(\w+)[^>]*>/g) || [];
  for (const tag of allTags) {
    const tagName = tag.match(/<(\w+)/)?.[1]?.toLowerCase();
    if (tagName && !ALLOWED_TAGS.includes(tagName) && !issues.some((issue) => issue.includes(tagName))) {
      issues.push(`Removed disallowed tag: <${tagName}>`);
    }
  }

  // Configure DOMPurify
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ALLOWED_TAGS,
    ALLOWED_ATTR: ALLOWED_ATTRIBUTES,
    RETURN_DOM: false,
    RETURN_TRUSTED_TYPE: false,
    WHOLE_DOCUMENT: false
  });

  const hadDangerousContent = clean !== html;

  return {
    html: clean,
    hadDangerousContent,
    issues,
    // If sanitized, it's considered valid/safe to render
    isValid: true
  };
}

/**
 * Normalize HTML by removing trailing spaces and extra whitespace
 * @param html - HTML to normalize
 * @returns Normalized HTML
 */
function normalizeHTML(html: string): string {
  return html
    .split("\n") // Split into lines
    .map((line) => line.trimEnd()) // Remove trailing spaces from each line
    .join("\n") // Join back
    .replaceAll(/\n\s*\n/g, "\n") // Remove empty lines with only whitespace
    .trim(); // Remove leading/trailing whitespace from entire string
}

/**
 * Validate HTML structure and content
 * @param html - HTML to validate
 * @returns Validation result with issues
 */
export function validateHTMLStructure(html: string): {
  isValid: boolean;
  issues: string[];
} {
  const issues: string[] = [];

  // Normalize HTML before validation (remove trailing spaces, etc)
  const normalizedHtml = normalizeHTML(html);

  // Check size (use original html for size check)
  if (html.length > MAX_HTML_SIZE) {
    issues.push(`Exceeds maximum size of ${MAX_HTML_SIZE} bytes`);
  }

  // Check for dangerous tags (use normalized)
  if (/<script\b/i.test(normalizedHtml)) {
    issues.push("Contains <script> tags which are not allowed");
  }

  if (/<iframe\b/i.test(normalizedHtml)) {
    issues.push("Contains <iframe> tags which are not allowed");
  }

  if (/<(object|embed)\b/i.test(normalizedHtml)) {
    issues.push("Contains <object> or <embed> tags which are not allowed");
  }

  // Check for event handlers (use normalized)
  if (/\s+on\w+\s*=/i.test(normalizedHtml)) {
    issues.push("Contains event handler attributes which are not allowed");
  }

  // Check for balanced tags (improved check - process tags in order)
  const voidElements = new Set(["br", "img", "input", "hr", "meta", "link"]);

  // Match all tags in order (opening, closing, and self-closing) - use normalized
  const allTags = normalizedHtml.match(/<\/?(\w+)[^>]*\/?>/g) || [];

  const tagStack: string[] = [];

  for (const tag of allTags) {
    // Check if it's a closing tag
    if (tag.startsWith("</")) {
      const tagName = tag.match(/<\/(\w+)/)?.[1]?.toLowerCase() || "";
      const lastOpen = tagStack.pop();

      if (lastOpen !== tagName) {
        issues.push(`HTML tags are not properly balanced (expected </${lastOpen || "nothing"}>, found </${tagName}>)`);
        break;
      }
    }
    // Check if it's a self-closing tag
    else if (tag.endsWith("/>")) {
      // Self-closing tags don't need to be added to stack
      continue;
    }
    // It's an opening tag
    else {
      const tagName = tag.match(/<(\w+)/)?.[1]?.toLowerCase() || "";

      // Only add to stack if not a void element
      if (!voidElements.has(tagName)) {
        tagStack.push(tagName);
      }
    }
  }

  if (tagStack.length > 0) {
    issues.push(`HTML tags are not properly balanced (unclosed tags: ${tagStack.join(", ")})`);
  }

  return {
    isValid: issues.length === 0,
    issues
  };
}

/**
 * Quick check if HTML contains any dangerous content
 * @param html - HTML to check
 * @returns true if dangerous content detected
 */
export function hasDangerousContent(html: string): boolean {
  const result = sanitizeHTML(html);
  return result.hadDangerousContent;
}

/**
 * Check if HTML is balanced (all tags properly closed)
 * @param html - HTML to check
 * @returns true if all tags are properly balanced
 */
export function isHTMLBalanced(html: string): boolean {
  const openTags = html.match(/<(\w+)[^>]*>/g) || [];
  const closeTags = html.match(/<\/(\w+)>/g) || [];
  const selfClosingTags = html.match(/<(\w+)[^>]*\/>/g) || [];

  // Extract tag names
  const openTagNames = openTags
    .map((tag) => {
      const match = tag.match(/<(\w+)/);
      return match?.[1]?.toLowerCase() || "";
    })
    .filter(Boolean);

  const closeTagNames = closeTags
    .map((tag) => {
      const match = tag.match(/<\/(\w+)/);
      return match?.[1]?.toLowerCase() || "";
    })
    .filter(Boolean);

  const selfClosingTagNames = new Set(
    selfClosingTags
      .map((tag) => {
        const match = tag.match(/<(\w+)/);
        return match?.[1]?.toLowerCase() || "";
      })
      .filter(Boolean)
  );

  // Void elements that don't need closing tags
  const voidElements = new Set(["br", "img", "input", "hr", "meta", "link"]);

  // Build tag stack
  const tagStack: string[] = [];
  for (const tagName of openTagNames) {
    if (!voidElements.has(tagName) && !selfClosingTagNames.has(tagName)) {
      tagStack.push(tagName);
    }
  }

  // Match closing tags
  for (const tagName of closeTagNames) {
    const lastOpen = tagStack.pop();
    if (lastOpen !== tagName) {
      return false; // Tags don't match
    }
  }

  // Check if all tags were closed
  return tagStack.length === 0;
}
